/**
 * Il tuo lavoro (28/10/2026): la pagina dei professionisti, cioè sviluppatori e
 * altri collaboratori che lavorano con noi da fuori.
 *
 * Lorenzo: "per ogni progetto, gli sviluppatori in particolare, ma qualsiasi
 * nostro collaboratore, potrà accedere, visualizzare i suoi progetti a cui sta
 * lavorando, le task e le scadenze".
 *
 * La domanda: cosa devo fare, e per quando? La forma: in alto la risposta in una
 * frase; a sinistra le sue task divise per urgenza (in ritardo, oggi, questa
 * settimana, più avanti), che si aprono per leggere i dettagli e si segnano fatte
 * con un tocco; a destra le scadenze in fila e i progetti su cui lavora, con chi
 * sentire. Mai cifre: niente valore del lavoro, costi o fatture (l'API non li
 * manda nemmeno: apps/api/src/modules/area).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { SectionMark } from '../../components/ui/SectionMark';
import { leggibile } from '../../components/mobile/MobileKit';
import './area.css';

type Stato = 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'BLOCKED' | 'DONE';
interface TaskArea {
  id: string;
  titolo: string;
  descrizione: string | null;
  stato: Stato;
  priorita: 'BASSA' | 'MEDIA' | 'ALTA';
  inizio: string | null;
  scadenza: string | null;
  fattaIl: string | null;
  inRitardo: boolean;
  progetto: { id: string; nome: string; cliente: string | null } | null;
  cliente: string | null;
  daChi: string;
  lista: Array<{ id: string; title: string; done: boolean }>;
}
interface ProgettoArea {
  id: string;
  nome: string;
  cliente: string | null;
  stato: string;
  avanzamento: number;
  inizio: string | null;
  consegna: string | null;
  referente: { nome: string; email: string } | null;
  ruolo: string | null;
  taskAperte: number;
  prossimaTappa: { titolo: string; data: string } | null;
}
interface ScadenzaArea { tipo: 'task' | 'progetto' | 'tappa'; id: string; data: string; titolo: string; progetto: string | null; scaduta: boolean }
interface AreaDati { oggi: string; task: TaskArea[]; progetti: ProgettoArea[]; scadenze: ScadenzaArea[] }

const areaQuery = { queryKey: ['area'] as const, queryFn: () => api.get<AreaDati>('/area') };

const STATO_PROGETTO: Record<string, string> = { PREVENTIVO: 'da partire', ATTIVO: 'attivo', IN_CORSO: 'in corso', REVISIONE: 'in revisione', IN_PAUSA: 'in pausa' };
const STATO: Record<Stato, string> = { TODO: 'Da fare', IN_PROGRESS: 'In corso', REVIEW: 'Da rivedere', BLOCKED: 'Bloccata', DONE: 'Fatta' };
/** I passi che può segnare da qui: "bloccata" no, vuole il motivo e va decisa con noi. */
const PASSI: Stato[] = ['TODO', 'IN_PROGRESS', 'REVIEW', 'DONE'];

const GIORNO = 86_400_000;
/** Il giorno di calendario a Roma ("2026-10-28"), come fa l'API (lib/dates.ts). */
const giornoRoma = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit' });
/**
 * Giorni da oggi a una data, sul calendario di Roma. "oggi" arriva a mezzanotte UTC;
 * le scadenze pure, ma "fatta il" ha l'ora: fatta alle 00:30 di Roma è oggi, non ieri.
 */
const giorniDa = (oggi: string, d: string) => Math.round((Date.parse(giornoRoma.format(new Date(d))) - Date.parse(oggi.slice(0, 10))) / GIORNO);
const dataBreve = (d: string) => new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', timeZone: 'Europe/Rome' });
const dataConGiorno = (d: string) => new Date(d).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Rome' });
const dataLunga = (d: string) => new Date(d).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Rome' });

/** "oggi", "domani", "ieri", "tra 3 giorni", "2 giorni fa". */
function traQuanto(oggi: string, d: string): string {
  const n = giorniDa(oggi, d);
  if (n === 0) return 'oggi';
  if (n === 1) return 'domani';
  if (n === -1) return 'ieri';
  return n < 0 ? `${-n} giorni fa` : `tra ${n} giorni`;
}
/** Come traQuanto, ma da una settimana in là conta il giorno: "mer 4 nov". */
const quando = (oggi: string, d: string) => (giorniDa(oggi, d) >= 7 ? dataConGiorno(d) : traQuanto(oggi, d));

/** Cosa scade, detto in una frase: la consegna dice di quale progetto. */
const cosaScade = (s: ScadenzaArea) =>
  (s.tipo === 'progetto' ? `consegna di ${s.progetto}` : s.tipo === 'tappa' && s.progetto ? `${s.titolo} (${s.progetto})` : s.titolo).replace(/[.\s]+$/, '');

/** Le task aperte divise per urgenza: la cosa da fare prima sta in cima. */
function gruppi(oggi: string, task: TaskArea[]) {
  const aperte = task.filter((t) => t.stato !== 'DONE');
  const g = (n: (t: TaskArea) => boolean) => aperte.filter(n);
  const fra = (t: TaskArea) => (t.scadenza ? giorniDa(oggi, t.scadenza) : null);
  return [
    { chiave: 'ritardo', titolo: 'In ritardo', rosso: true, task: g((t) => t.inRitardo) },
    { chiave: 'oggi', titolo: 'Oggi', rosso: false, task: g((t) => fra(t) === 0) },
    { chiave: 'settimana', titolo: 'Prossimi 7 giorni', rosso: false, task: g((t) => { const n = fra(t); return n != null && n > 0 && n <= 7; }) },
    { chiave: 'avanti', titolo: 'Più avanti', rosso: false, task: g((t) => { const n = fra(t); return n != null && n > 7; }) },
    { chiave: 'senza', titolo: 'Senza scadenza', rosso: false, task: g((t) => t.scadenza == null) },
  ].filter((x) => x.task.length > 0);
}

export function AreaPage() {
  const nome = useAuthStore((s) => s.user?.firstName);
  const q = useQuery(areaQuery);
  const [params, setParams] = useSearchParams();
  const daAprire = params.get('task');
  const [linkPerso, setLinkPerso] = useState(false);

  const d = q.data;
  const aperte = d ? d.task.filter((t) => t.stato !== 'DONE') : [];
  const fatte = d ? d.task.filter((t) => t.stato === 'DONE') : [];

  // Il link di una mail (/tasks/:id → /area?task=:id) vale una volta: la task si è
  // aperta ed è in vista (RigaTask), il parametro si toglie. Così non si riapre da
  // sola quando passa tra le fatte. Se non è tra le sue, lo si dice.
  useEffect(() => {
    if (!d || !daAprire) return;
    setLinkPerso(!d.task.some((t) => t.id === daAprire));
    setParams((p) => { const n = new URLSearchParams(p); n.delete('task'); return n; }, { replace: true });
  }, [d, daAprire, setParams]);

  return (
    <div className="page ar-page">
      <header className="ar-hd">
        <SectionMark icon="tasks" className="ar-sez" />
        <div className="min-w-0">
          <h1 className="ar-title">{nome ? `Ciao ${nome}` : 'Il tuo lavoro'}</h1>
          {d && <p className="ar-risposta"><Risposta d={d} aperte={aperte} /></p>}
          {linkPerso && <p className="ar-nota">La task del link non è più tra le tue.</p>}
          {q.isError && <p className="ar-risposta">Non riesco a leggere il tuo lavoro in questo momento: riprova tra poco.</p>}
        </div>
      </header>

      {d && (
        <div className="ar-cols">
          <section className="ar-blocco" aria-label="Le tue task">
            <h2 className="ar-h2"><Icon name="tasks" size={14} /> Le tue task</h2>
            {aperte.length === 0 && <p className="ar-vuoto">Niente da fare adesso.</p>}
            {gruppi(d.oggi, d.task).map((g) => (
              <div key={g.chiave} className="ar-gruppo">
                <h3 className={`ar-h3${g.rosso ? ' ar-rosso' : ''}`}>{g.titolo} <span>{g.task.length}</span></h3>
                {g.task.map((t) => <RigaTask key={t.id} t={t} oggi={d.oggi} aperta={t.id === daAprire} />)}
              </div>
            ))}
            {fatte.length > 0 && <Fatte task={fatte} oggi={d.oggi} daAprire={daAprire} />}
          </section>

          <div className="ar-lato">
            <Scadenze d={d} />
            <Progetti d={d} />
          </div>
        </div>
      )}
    </div>
  );
}

/** La frase in alto: quante task aperte, quante in ritardo, la prossima scadenza. */
function Risposta({ d, aperte }: { d: AreaDati; aperte: TaskArea[] }) {
  const n = aperte.length;
  if (n === 0) return <>Non hai task aperte. Quando ti assegniamo un lavoro lo trovi qui, con la sua scadenza.</>;
  const ritardo = aperte.filter((t) => t.inRitardo).length;
  // I progetti delle task aperte, non tutti quelli della pagina (squadra, task fatte).
  const progetti = new Set(aperte.flatMap((t) => (t.progetto ? [t.progetto.id] : []))).size;
  const prossima = d.scadenze.find((s) => !s.scaduta);
  return (
    <>
      Hai <strong>{n === 1 ? '1 task aperta' : `${n} task aperte`}</strong>
      {n > 1 && progetti > 0 && <> su {progetti === 1 ? '1 progetto' : `${progetti} progetti`}</>}
      {ritardo > 0 && (n === 1
        ? <> ed è <strong className="ar-rosso">in ritardo</strong></>
        : <>, <strong className="ar-rosso">{ritardo === n ? 'tutte in ritardo' : `${ritardo} in ritardo`}</strong></>)}.
      {prossima && <> La prossima scadenza è <strong>{giorniDa(d.oggi, prossima.data) >= 7 ? dataLunga(prossima.data) : traQuanto(d.oggi, prossima.data)}</strong>: {cosaScade(prossima)}.</>}
    </>
  );
}

function RigaTask({ t, oggi, aperta: apertaDaLink }: { t: TaskArea; oggi: string; aperta?: boolean }) {
  const qc = useQueryClient();
  const [aperta, setAperta] = useState(!!apertaDaLink);
  const fatta = t.stato === 'DONE';
  // Ferma (BLOCKED): la fa ripartire chi l'ha fermata, non da qui.
  const ferma = t.stato === 'BLOCKED';

  // Arrivato da una mail o una notifica (/tasks/:id): la task si apre e si porta in vista.
  useEffect(() => {
    if (apertaDaLink) document.getElementById(`ar-t-${t.id}`)?.scrollIntoView({ block: 'center' });
  }, [apertaDaLink, t.id]);

  const stato = useMutation({
    mutationFn: (s: Stato) => api.patch(`/area/task/${t.id}`, { status: s }),
    onSuccess: (_r, s) => {
      qc.invalidateQueries({ queryKey: ['area'] });
      // Fatta, la riga sparisce dall'elenco: si dice dove ritrovarla.
      if (s === 'DONE') toast.ok('Task segnata fatta', 'La ritrovi in Fatte di recente.');
      else toast.ok(`Ora è ${STATO[s].toLowerCase()}`, t.titolo);
    },
    onError: (e) => {
      toast.alert('Non salvato', e instanceof ApiError ? e.message : 'Riprova tra qualche secondo.');
      // Rifiutato dall'API (409: fermata mentre la pagina era aperta): si rilegge il lavoro,
      // così al posto dei pulsanti compare "Ferma: per farla ripartire senti …".
      if (e instanceof ApiError) qc.invalidateQueries({ queryKey: ['area'] });
    },
  });
  const voce = useMutation({
    mutationFn: (v: { id: string; done: boolean }) => api.patch(`/area/task/${t.id}/lista/${v.id}`, { done: v.done }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['area'] }),
    onError: (e) => toast.alert('Non salvato', e instanceof ApiError ? e.message : 'Riprova tra qualche secondo.'),
  });

  const cliente = t.progetto ? t.progetto.cliente : t.cliente;
  const fatteLista = t.lista.filter((v) => v.done).length;

  return (
    <div id={`ar-t-${t.id}`} className={`ar-task${aperta ? ' is-aperta' : ''}${fatta ? ' is-fatta' : ''}`}>
      <div className="ar-task-riga">
        <button
          type="button"
          className={`ar-spunta${fatta ? ' is-on' : ''}`}
          aria-label={ferma ? `Ferma: ${t.titolo}` : `${fatta ? 'Riapri' : 'Segna fatta'}: ${t.titolo}`}
          title={ferma ? `Ferma: per farla ripartire senti ${t.daChi}` : fatta ? 'Riapri' : 'Segna fatta'}
          disabled={stato.isPending || ferma}
          onClick={() => stato.mutate(fatta ? 'TODO' : 'DONE')}
        >
          {fatta && <Icon name="check" size={13} />}
        </button>
        <button type="button" className="ar-task-testo" onClick={() => setAperta((v) => !v)} aria-expanded={aperta}>
          <span className="t">{t.titolo}</span>
          <span className="s">
            {(t.progetto || cliente) && (
              <span>
                {t.progetto?.nome}{t.progetto && cliente && ' · '}{cliente && <span className="ar-nw">{leggibile(cliente)}</span>}
              </span>
            )}
            {t.stato === 'IN_PROGRESS' || t.stato === 'REVIEW' || t.stato === 'BLOCKED' ? <span className="ar-chip">{STATO[t.stato]}</span> : null}
            {t.priorita === 'ALTA' && !fatta && <span className="ar-chip ar-chip--alta">Priorità alta</span>}
            {t.lista.length > 0 && <span>{fatteLista} di {t.lista.length} punti</span>}
          </span>
        </button>
        {/* Senza scadenza niente: lo dice già il gruppo. */}
        {(fatta ? t.fattaIl : t.scadenza) && (
          <span className={`ar-task-data${t.inRitardo ? ' ar-rosso' : ''}`}>
            {fatta
              ? `fatta ${quando(oggi, t.fattaIl!)}`
              : `${t.inRitardo ? 'scaduta' : 'scade'} ${quando(oggi, t.scadenza!)}`}
          </span>
        )}
      </div>

      {aperta && (
        <div className="ar-task-corpo">
          {t.descrizione ? <p className="ar-descr">{t.descrizione}</p> : <p className="ar-vuoto">Nessuna descrizione: per i dettagli senti {t.daChi}.</p>}
          {t.lista.length > 0 && (
            <ul className="ar-lista">
              {t.lista.map((v) => (
                <li key={v.id}>
                  <label>
                    <input type="checkbox" checked={v.done} disabled={voce.isPending} onChange={(e) => voce.mutate({ id: v.id, done: e.target.checked })} />
                    <span className={v.done ? 'is-fatta' : ''}>{v.title}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <div className="ar-task-piede">
            {/* Una task ferma la fa ripartire chi l'ha fermata (l'API rifiuta il cambio di stato da qui). */}
            {ferma ? (
              <p className="ar-ferma"><Icon name="alert" size={12} /> Ferma: per farla ripartire senti {t.daChi}.</p>
            ) : (
              <div className="ar-passi" role="group" aria-label="A che punto sei">
                {PASSI.map((s) => (
                  <button key={s} type="button" className={`ar-passo${t.stato === s ? ' is-on' : ''}`} aria-pressed={t.stato === s} disabled={stato.isPending || t.stato === s} onClick={() => stato.mutate(s)}>
                    {STATO[s]}
                  </button>
                ))}
              </div>
            )}
            <span className="ar-dachi">
              Assegnata da {t.daChi}
              {t.scadenza && <> · scadenza {dataConGiorno(t.scadenza)}</>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/** Le task chiuse negli ultimi 14 giorni: chiuse, si riaprono se servono. */
function Fatte({ task, oggi, daAprire }: { task: TaskArea[]; oggi: string; daAprire: string | null }) {
  const [aperto, setAperto] = useState(task.some((t) => t.id === daAprire));
  return (
    <div className="ar-gruppo">
      <button type="button" className="ar-h3 ar-fatte" onClick={() => setAperto((v) => !v)} aria-expanded={aperto}>
        Fatte di recente <span>{task.length}</span> <Icon name="chevronR" size={12} className={aperto ? 'ar-giu' : ''} />
      </button>
      {aperto && task.map((t) => <RigaTask key={t.id} t={t} oggi={oggi} aperta={t.id === daAprire} />)}
    </div>
  );
}

// La task con l'icona delle task: la spunta da sola sembrava "già fatta".
const ICONA_SCADENZA: Record<ScadenzaArea['tipo'], IconName> = { task: 'tasks', progetto: 'cube', tappa: 'pin' };

function Scadenze({ d }: { d: AreaDati }) {
  const [tutte, setTutte] = useState(false);
  const elenco = tutte ? d.scadenze : d.scadenze.slice(0, 8);
  return (
    <section className="ar-blocco" aria-label="Scadenze">
      <h2 className="ar-h2"><Icon name="calendar" size={14} /> Scadenze</h2>
      {d.scadenze.length === 0 ? (
        <p className="ar-vuoto">Nessuna scadenza nei prossimi due mesi.</p>
      ) : (
        <ol className="ar-scadenze">
          {elenco.map((s) => {
            const data = new Date(s.data);
            return (
              <li key={`${s.tipo}-${s.id}`} className={s.scaduta ? 'is-scaduta' : ''}>
                <span className="ar-giorno" aria-hidden>
                  <b>{data.toLocaleDateString('it-IT', { day: 'numeric', timeZone: 'Europe/Rome' })}</b>
                  <i>{data.toLocaleDateString('it-IT', { month: 'short', timeZone: 'Europe/Rome' })}</i>
                </span>
                <span className="ar-sc-testo">
                  <span className="t"><Icon name={ICONA_SCADENZA[s.tipo]} size={11} /><span>{s.titolo}</span></span>
                  {/* Prima quando (la data è già nel riquadro), poi il progetto: se non ci sta, si accorcia il progetto. */}
                  <span className="s">{s.scaduta ? 'scaduta ' : ''}{traQuanto(d.oggi, s.data)}{s.progetto ? ` · ${s.progetto}` : ''}</span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {d.scadenze.length > 8 && (
        <button type="button" className="ar-link" onClick={() => setTutte((v) => !v)}>{tutte ? 'Mostra meno' : `Mostra tutte (${d.scadenze.length})`}</button>
      )}
    </section>
  );
}

function Progetti({ d }: { d: AreaDati }) {
  const progetti = d.progetti;
  return (
    <section className="ar-blocco" aria-label="I tuoi progetti">
      <h2 className="ar-h2"><Icon name="cube" size={14} /> I tuoi progetti</h2>
      {progetti.length === 0 ? (
        <p className="ar-vuoto">Nessun progetto per ora: compaiono quando ti assegniamo una task o ti mettiamo nella squadra.</p>
      ) : (
        <div className="ar-progetti">
          {progetti.map((p) => (
            <article key={p.id} className="ar-progetto">
              <div className="ar-pr-hd">
                <span className="t">{p.nome}</span>
                {(p.cliente || p.ruolo) && <span className="s">{[p.cliente && leggibile(p.cliente), p.ruolo].filter(Boolean).join(' · ')}</span>}
              </div>
              <div className="ar-pr-bar">
                <span className="ar-bar" aria-hidden><i style={{ width: `${Math.max(0, Math.min(100, p.avanzamento))}%` }} /></span>
                <span className="l">{p.avanzamento}% fatto{STATO_PROGETTO[p.stato] ? ` · ${STATO_PROGETTO[p.stato]}` : ''}</span>
              </div>
              <dl className="ar-pr-dati">
                <div><dt>Le tue task aperte</dt><dd>{p.taskAperte}</dd></div>
                {p.consegna && <div><dt>Consegna</dt><dd>{dataBreve(p.consegna)}</dd></div>}
                {p.prossimaTappa && <div><dt>Prossima tappa</dt><dd>{p.prossimaTappa.titolo}, <span className="ar-nw">{dataBreve(p.prossimaTappa.data)}</span></dd></div>}
                {p.referente && <div><dt>Per dubbi</dt><dd><a href={`mailto:${p.referente.email}`}>{p.referente.nome}</a></dd></div>}
              </dl>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
